import { createStore } from "vuex";
import axios, { AxiosRequestConfig } from "axios";
import MD5 from "@/lib/md5";
import router from "@/router";
import VuexPersistence from "vuex-persist";
import util from "@/lib/util";
import { setupAxiosInterceptor } from "@/lib/axiosInterceptor";

// Add timeout to prevent API calls from hanging in MAUI
axios.defaults.timeout = 30000;

// Lazy-imported to avoid a circular dependency at module load time
// (lib/auth0.ts imports AppSettings from this file). Used by the
// generatecustomtokeninauth0 action below — the global axios interceptor
// imports auth0 helpers directly from its own module since it sits
// outside the store ↔ auth0 cycle.
type Auth0Helpers = typeof import("@/lib/auth0");
let auth0HelpersPromise: Promise<Auth0Helpers> | null = null;
const loadAuth0Helpers = (): Promise<Auth0Helpers> => {
  if (!auth0HelpersPromise) {
    auth0HelpersPromise = import("@/lib/auth0");
  }
  return auth0HelpersPromise;
};

const vuexLocal = new VuexPersistence({
  storage: window.localStorage,
});

export const AppSettings = {
  //Dev Auth-O setting
  //auth0Domain: "dev-ewe301ksh1ppj8ey.us.auth0.com",
  //auth0CliientId: "VpB1kMuSQXwgjXZMTj3IWxByrI9wukEq",

  //PROD Auth-O setting
  auth0Domain: "hsnotify.us.auth0.com",
  auth0CliientId: "IWMINcPB7XXurePAlWUdZw1w4XL4nmsZ",

  //API_HOST: "http://localhost:8040/", //// Local
  //API_HOST: "api_url_frontend",
  //API_HOST: "https://lou-dev-notify-lb-api.hellospoke.com/",
  //API_HOST: "https://lou-qa-notify-lb-api.hellospoke.com/",
  //API_HOST: "https://lou-dev2-notify-lb-api.hellospoke.com/",
  // API_HOST: "https://lou-notify-lb-api.hellospoke.com/",
  // Must match notify-web-upgrade/.env.qa VITE_API_HOST. The web1 host is
  // the dashboard/websocket front door — posting Auth0 token-mint and
  // presence calls there times out and force-logouts after login.
  //API_HOST: "https://lou-qa-notify-lb1-new-api.hellospoke.com/",
   API_HOST: "https://lou-notify-lb1-core-api.hellospoke.com/",

  //QA AWS SETTINGS,
  //_MESSAGE_RECORDING_URL: "hsnotifymessagerecordingqa/", //'https://hsnotifydev.s3-us-west-2.amazonaws.com/',
  //_LIVE_RECORDING_URL: "notifyrecordingsqa",
  //_VOICE_MAIL_RECORDING_URL: "hsnotifyqa",
  //_AI_CALL_BACK_URL: "notify-ai-callback-qa",
  //messageWebSocket: "https://lou-qa-notify-web1-new.hellospoke.com:4444", //qa
  //MASS_CALL_NUMBER: "502-233-3695", //qa

  // DEV AWS SETTINGS,
  //_MESSAGE_RECORDING_URL: "hsnotifymessagerecordingdev/", //'https://hsnotifydev.s3-us-west-2.amazonaws.com/',
  //_LIVE_RECORDING_URL: "notifyrecordingsdev",
  //_VOICE_MAIL_RECORDING_URL: "hsnotifydev",
  //_AI_CALL_BACK_URL: "notify-ai-callback-dev",
  //messageWebSocket: "https://lou-dev-notify-web1.hellospoke.com:4444", //dev
  //MASS_CALL_NUMBER: "502-555-0448", //dev

  // DEV2 AWS SETTINGS,
  // _MESSAGE_RECORDING_URL: "hsnotifymessagerecordingdev2/", //'https://hsnotifydev.s3-us-west-2.amazonaws.com/',
  // _LIVE_RECORDING_URL: "notifyrecordingsdev2",
  // _VOICE_MAIL_RECORDING_URL: "hsnotifydev2",
  // _AI_CALL_BACK_URL: "notify-ai-callback-dev2",
  // messageWebSocket: "https://lou-dev2-notify-web1.hellospoke.com:4444", //dev
  // MASS_CALL_NUMBER: "502-555-0200", //dev

  //PROD PORODUCTION SETTINGS,
  //_MESSAGE_RECORDING_URL: "hsnotifymessagerecording/", //'https://hsnotifydev.s3-us-west-2.amazonaws.com/',
  //_LIVE_RECORDING_URL: "notifyrecordings",
  //_VOICE_MAIL_RECORDING_URL: "hsnotify",
  //_AI_CALL_BACK_URL: "notify-ai-callback-prod",
  //messageWebSocket: "https://lou-notify-web1.hellospoke.com:4242", //prod
  //MASS_CALL_NUMBER: "502-383-3332", //prod

  //New PROD PORODUCTION SETTINGS,
  _MESSAGE_RECORDING_URL: "hsnotifymessagerecording/", //'https://hsnotifydev.s3-us-west-2.amazonaws.com/',
  _LIVE_RECORDING_URL: "notifyrecordings",
  _VOICE_MAIL_RECORDING_URL: "hsnotify",
  _AI_CALL_BACK_URL: "notify-ai-callback-prod",
  messageWebSocket: "https://lou-notify-web1-core.hellospoke.com:4242", //prod
  MASS_CALL_NUMBER: "502-383-3332", //prod
};

// 401s are owned end-to-end by the global axios interceptor wired up below
// via setupAxiosInterceptor() — see src/lib/axiosInterceptor.ts. That
// module silently regenerates the server JWT and replays the original
// request; it only force-logouts when Auth0 itself confirms the session
// is dead (login_required / missing_refresh_token / invalid_grant).
// This helper now only handles the non-401 cases that were always its
// real value-add: network drops and hung requests.
const handle401 = (r: any, _context: any) => {
  if (r && r.response && (r.response.status == "401" || r.response.status === 401)) {
    // No-op: handled (or already failed) by the interceptor.
    return;
  }
  if (!r?.response && (r?.message === "Network Error" || r?.code === "NETWORK_ERROR")) {
    console.warn("Network error detected, reloading page...");
    window.location.reload();
    return;
  }
  if (r?.message && typeof r.message === "string" && r.message.includes("timeout")) {
    console.warn("Timeout error detected, reloading page...");
    window.location.reload();
  }
};

const initialState = {
  account: {
    jwt: "",
  },
  notifyData: {
    login_company_id: "",
    username: "",
    user: "",
    user_type: "",
    propertyList: [] as any,
    isRestrcitedUser: false,
    loginPrompt: "none",
    redirect: false,
    redirecttype: "",
    redirectpropertyid: "",
    redirectcompanyid: "",
    redirectguid: "",
    // The call_report_data/guid detail endpoint (ICOView) doesn't return escalationtype /
    // resolution_status the way the elasticsearch list rows (CallActivityView) do, so we
    // stash the tapped row's copy here and ICOView falls back to it when its own fetch is
    // missing those fields. Only populated when navigating from the call list itself —
    // deep links (push notification / login redirect) leave this null, which is fine since
    // there's no list row to source it from.
    redirectCallSummary: null as any,
    residents: [],
    company_properties: [],
    notifypropertyreportdata: {},
    yardi_residents: [],
    entrata_residents: [],
    resman_residents: [],
    communicationUserchatList: null as any,
    activeChat: null as any,
    current_user: null as any,
    userSetting: null as any,
    user_info: null as any,
    notify_residents: [],
    optinstatusData: [],
    communication: null as any,
    getCommunicationHistory: null as any,
    directChatParams: {
      propertyid: "",
      channelId: "",
    },
    userDetailsIco: {
      phone: "",
      navigation_from_workorder: false,
    },
  },
  activeProperty: null,
  activeSchedule: null,
  activeDate: null,
  activeNotifyResidents: null,
  nonoptinResidents: null,
};

const store = createStore({
  plugins: [vuexLocal.plugin],
  state: { ...initialState },
  mutations: {
    RESET_APP_STATE: (state) => {
      Object.assign(state, initialState);
    },

    LOGIN_SUCCESS: (state, payload) => {
      window.localStorage.setItem("id_token", payload.data.token);
      state.account.jwt = payload.data.token;
      axios.defaults.headers.common.Authorization = "Bearer " + payload.data.token;
    },

    SET_USERNAME: (state, payload) => {
      state.notifyData.username = payload;
    },

    SET_IS_RESTRICTED_USER: (state, payload) => {
      state.notifyData.isRestrcitedUser = payload;
      state.notifyData.loginPrompt = payload ? "login" : "none";
    },

    SET_LOGIN_COMPANY_ID: (state, payload) => {
      state.notifyData.login_company_id = payload;
    },
    SEND_MESSAGE: (state, payload) => {
      state.notifyData.communication = payload;
    },

    SET_NOTIFY_USER_TYPE: (state, payload) => {
      if (payload.user_type === undefined) state.notifyData.user_type = "setupadmin";
      else if (payload.user_type) {
        state.notifyData.user_type = payload.user_type;
      } else {
        state.notifyData.user_type = "other";
      }
    },

    SET_CURRENT_USER_DETAIL: (state, payload) => {
      state.notifyData.current_user = payload;
    },
    SET_USER_SETTINGS: (state, payload) => {
      state.notifyData.userSetting = payload;
    },

    SET_NOTIFY_USER_PROPERTYLIST: (state, payload) => {
      state.notifyData.propertyList = payload.propertylist;
    },

    SET_ACTIVE_PROPERTY: (state, payload) => {
      state.activeProperty = payload;
    },
    SET_ACTIVE_NOTIFYRESIDENT: (state, payload) => {
      state.activeNotifyResidents = payload;
    },
    SET_NONOPTIN_NOTIFYRESIDENT: (state, payload) => {
      state.nonoptinResidents = payload;
    },
    SET_ACTIVE_SCHEDULE: (state, payload) => {
      state.activeSchedule = payload;
    },

    SET_ACTIVE_DATE: (state, payload) => {
      state.activeDate = payload;
    },
    //Code for redirect through login
    SET_REDIRECT: (state, payload) => {
      state.notifyData.redirect = payload;
    },
    SET_REDIRECT_TYPE: (state, payload) => {
      state.notifyData.redirecttype = payload;
    },
    SET_REDIRECT_PROPERTY_ID: (state, payload) => {
      state.notifyData.redirectpropertyid = payload;
    },
    SET_REDIRECT_COMPANY_ID: (state, payload) => {
      state.notifyData.redirectcompanyid = payload;
    },
    SET_REDIRECT_GUID: (state, payload) => {
      state.notifyData.redirectguid = payload;
    },
    SET_REDIRECT_CALL_SUMMARY: (state, payload) => {
      state.notifyData.redirectCallSummary = payload;
    },
    //Code for resident
    SET_RESIDENTS: (state, payload) => {
      state.notifyData.residents = payload;
    },
    SET_NOTIFY_REPORT_DATA: (state, payload) => {
      state.notifyData.notifypropertyreportdata = payload.data.result;
    },
    SET_YARDI_RESIDENTS: (state, payload) => {
      state.notifyData.yardi_residents = payload;
    },
    SET_ENTRATA_RESIDENTS: (state, payload) => {
      state.notifyData.entrata_residents = payload;
    },
    SET_RESMAN_RESIDENTS: (state, payload) => {
      state.notifyData.resman_residents = payload;
    },
    SET_USERCHATLIST: (state, payload) => {
      state.notifyData.communicationUserchatList = payload;
    },
    SET_USEROPTIN_STATUS: (state, payload) => {
      state.notifyData.optinstatusData = payload;
    },
    RESET_REDIRECT_STATE: (state, payload) => {
      state.notifyData.redirect = false;
      state.notifyData.redirecttype = "";
      state.notifyData.redirectpropertyid = "";
      state.notifyData.redirectcompanyid = "";
      state.notifyData.redirectguid = "";
      state.notifyData.redirectCallSummary = null;
    },
    SET_NOTIFY_RESIDENTS: (state, payload) => {
      state.notifyData.notify_residents = payload;
    },
    SET_ACTIVE_CHAT: (state, payload) => {
      state.notifyData.activeChat = payload;
    },

    SET_USERCHATHISTORY: (state, payload) => {
      if (!payload?.data?.results?.chat) {
        payload.data.results.chat = [];
      }
      state.notifyData.getCommunicationHistory = payload;
    },

    CLEAR_CHAT_HISTORY: (state) => {
      state.notifyData.getCommunicationHistory = null;
    },

    UPDATE_CHAT_HISTORY: (state, payload) => {
      if (state.notifyData.getCommunicationHistory) {
        state.notifyData.getCommunicationHistory.data.results.chat.push(payload);
      }
    },

    MARK_CHAT_AS_READ: (state, payload) => {
      state.notifyData.communicationUserchatList.forEach((ucl: any) => {
        if (ucl._source.channelId == payload.channelID) {
          ucl._source.readStatus = "read";
          ucl._source.unreadCount = 0;
        }
      });
    },

    APPLY_UNREAD_COUNTS: (state, countMap: Record<string, number>) => {
      state.notifyData.communicationUserchatList.forEach((item: any) => {
        const channelId = item._source?.channelId;
        if (channelId in countMap) {
          // We have explicit data from the notification API — trust it
          const count = countMap[channelId];
          item._source.unreadCount = count;
          item._source.readStatus = count > 0 ? "unread" : "read";
        } else {
          // Channel not in notification data — mark as read (no unread notifications)
          item._source.unreadCount = 0;
          item._source.readStatus = "read";
        }
      });
    },

    SET_DIRECT_CHAT_PARAMS: (state, payload) => {
      state.notifyData.directChatParams = {
        propertyid: payload.propertyid,
        channelId: payload.channelId,
      };
    },
    SET_ACTIVE_RESIDENTPHONENUMBER: (state, payload) => {
      state.notifyData.userDetailsIco = {
        phone: payload.phone,
        navigation_from_workorder: payload.navigation_from_workorder,
      };
    },
  },
  actions: {
    // Calls the backend to mint a brand new server JWT for the given Auth0
    // user. Authenticated with the *Auth0 access_token* (NOT the possibly-
    // revoked server JWT) so that this call still works after the server
    // has invalidated the old server JWT. The interceptor above relies on
    // this action to silently recover from 401s.
    generatecustomtokeninauth0: async (_context, payload: { email: string }) => {
      const helpers = await loadAuth0Helpers();
      const auth0AccessToken = await helpers.getFreshAuth0AccessToken();
      const r: any = await axios.post(`${AppSettings.API_HOST}api/v1/generatecustomtokeninauth0`, { payload: { email: payload.email } }, { headers: { Authorization: "Bearer " + auth0AccessToken } });
      // Backend returns the new server JWT either as a string or wrapped
      // in { token: "..." } depending on environment — handle both.
      const token: string = typeof r?.data === "string" ? r.data : r?.data?.token || r?.data?.id_token || r?.data?.custom_token || "";
      return token;
    },

    loginRequest: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios
          .post(`${AppSettings.API_HOST}login`, {
            creds: MD5(`${payload.username.toLowerCase()}:${payload.password}`),
            account_name: payload.account_name,
          })
          .then(async (r: any) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    GetPrimaryAccountName: async (context, payload) => {
      const email = payload.email;

      return new Promise((resolve, reject) => {
        axios
          .get(`${AppSettings.API_HOST}login/${email}`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              reject(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    notify_forgot_password: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}forgotpassword/company/${payload.data.accountname}/user/${payload.data.email}`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    notifyresetpassword: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        payload.creds = MD5(`${payload.data.username}:${payload.data.password}`);
        axios
          .put(`${AppSettings.API_HOST}resetPassword`, {
            payload: payload,
          })
          .then((r) => {
            if (((r as any).data && (r as any).data.error === "401") || (r as any).data == "Error") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            // context.commit('SHOW_CONTACT_INFOLOADER', false);

            reject(r);
          });
      });
    },

    notifychangepassword: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}savepassword/company/${payload.data.companyid}/${payload.data.accountname}/user/${payload.data.userid}/password/${payload.data.password}`, {})
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    getPresence: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}presenceid/${payload.username}`)
          .then((r) => {
            resolve(r);
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    getNotifyProperties: (context, payload) => {
      return new Promise(async (resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}companies/${payload}/properties/added`)
          .then(async (r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              await (r as any).data.docs.sort((a: any, b: any) => {
                return a.propertyname.toLowerCase() > b.propertyname.toLowerCase() ? 1 : -1;
              });
              resolve(r);
            } else {
              await (r as any).data.docs.sort((a: any, b: any) => {
                return a.propertyname.toLowerCase() > b.propertyname.toLowerCase() ? 1 : -1;
              });
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    getPropertyDetail: (context, payload) => {
      return new Promise(async (resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}companies/${payload.companyid}/properties/${payload.propertyid}/propertyinfo`)
          .then(async (r) => {
            resolve(r);
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    getPropertySchedule(context, payload) {
      return new Promise(async (resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        const config: AxiosRequestConfig = {
          params: { start_date: payload.date },
        };
        axios
          .get(`${AppSettings.API_HOST}companies/${payload.companyid}/properties/${payload.propertyid}/mschedules/${payload.optiontype}/schtype/${payload.schtype}`, config)
          .then(async (r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    getPropertyUsers: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}companies/${payload.companyid}/property/${payload.propertyid}/users/${payload.page}/${payload.limit}/${payload.activeuseronly}`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    savePropertySchedule(context, payload) {
      const companyid = payload.companyid;
      const propertyid = payload.propertyid;
      return new Promise(async (resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .post(`${AppSettings.API_HOST}companies/${companyid}/properties/${propertyid}/dayschedules`, {
            payload: JSON.stringify(payload),
          })
          .then(async (r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    notifyusercolorindexsave: (context, payload) => {
      return new Promise((resolve, reject) => {
        var uid = payload.userid;
        var propertyid = payload.propertyid;
        var companyid = payload.companyid;
        var colorindex = payload.colorindex;

        axios
          .put(`${AppSettings.API_HOST}company/${companyid}/property/${propertyid}/user/${uid}/${colorindex}`, {
            payload: JSON.stringify(payload),
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    //API calling for residents
    GetRealpageResidents: (context, payload) => {
      const propertyid = payload.propertyid;
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}residents/${propertyid}`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              reject(r);
            } else {
              context.commit("SET_RESIDENTS", (r as any).data);
              resolve(r);
            }
          })
          .catch((r) => {
            //context.commit("SHOW_RESIDENT_INFOLOADER", false);
            reject(r);
          });
      });
    },
    GetYardiResidents: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .post(`${AppSettings.API_HOST}yardi_company/property/residents`, {
            payload: payload,
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              reject(r);
            } else {
              context.commit("SET_YARDI_RESIDENTS", (r as any).data);
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    GetResmanResidents: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .post(`${AppSettings.API_HOST}notify/property/residents`, {
            payload: payload,
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              reject(r);
            } else {
              context.commit("SET_RESMAN_RESIDENTS", (r as any).data);
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    getResmanBuildings: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + localStorage.id_token;
        axios
          .post(`${AppSettings.API_HOST}resman/company/property/data`, {
            payload: JSON.stringify(payload),
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              reject(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    getResmanSites: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .post(`${AppSettings.API_HOST}resman/company/get_site`, {
            payload: JSON.stringify(payload),
          })
          .then((r) => {
            resolve(r);
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    getYardiSite: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .post(`${AppSettings.API_HOST}yardy/company/get_site`, {
            payload: JSON.stringify(payload),
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              //context.commit('SET_YARDI_COMPANY_PROPERTY_WORK_ORDER', r.data.data);
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    GetPropertyResidentByPhone(context, payload) {
      const { property_id, phone_number } = payload;
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}property/${property_id}/phonenumber/${phone_number}/resident`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              reject(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    elasticsearch: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .post(`${AppSettings.API_HOST}elasticsearch_mobile`, {
            payload: payload,
            json: true,
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              handle401(r, context);
            } else {
              context.commit("SET_NOTIFY_REPORT_DATA", r);
              resolve(r.data);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    getNotifyUserforclicktocall: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}getNotifyUserforclicktocall/${payload.uid}`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              handle401(r, context);
              reject(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    freeswitch_click_to_call: async (context, payload) => {
      const propertyid = payload.propertyid;
      const guid = payload.guid;
      //console.log('state.notifyData.current_user---------',context.state.notifyData.current_user);
      const currentUser = context.state.notifyData.current_user;
      const settingsResponse: any = await context.dispatch("getNotifyUserSettings", { uid: currentUser?.id });
      //console.log('settingsResponse---------',settingsResponse);
      const settingsData = settingsResponse?.data?.data ?? settingsResponse?.data;
      const userinfo = { ...currentUser, notificationrulessetting: settingsData?.notificationrulessetting };

      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .post(`${AppSettings.API_HOST}freeswitch_click_to_call/${propertyid}/guid/${guid}`, { payload: userinfo })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              handle401(r, context);
              reject(r);
            } else {
              console.log("freeswitch_click_to_call-------", r);
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    get_work_order_details: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}property/${payload.propertyid}/orders/${payload.guid}`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              handle401(r, context);
              resolve(r);
            } else {
              //alert(JSON.stringify(r));
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    get_yardi_work_order_details: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}property/${payload.propertyid}/yardi/orders/${payload.guid}`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              handle401(r, context);
              resolve(r);
            } else {
              //alert(JSON.stringify(r));
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    get_resman_work_order_details: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}property/${payload.propertyid}/resman/orders/${payload.guid}`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              handle401(r, context);
              resolve(r);
            } else {
              //alert(JSON.stringify(r));
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    update_work_order_status: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .patch(`${AppSettings.API_HOST}property/${payload.propertyid}/orders/${payload.guid}`, {
            payload: JSON.stringify(payload),
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    update_entrata_work_order_status: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .patch(`${AppSettings.API_HOST}property/${payload.propertyid}/entrata/status/orders/${payload.guid}`, {
            payload: JSON.stringify(payload),
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    update_yardi_work_order_status: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .patch(`${AppSettings.API_HOST}property/${payload.propertyid}/yardi/status/orders/${payload.guid}`, {
            payload: JSON.stringify(payload),
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    update_resman_work_order_status: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .patch(`${AppSettings.API_HOST}property/${payload.propertyid}/resman/status/orders/${payload.guid}`, {
            payload: JSON.stringify(payload),
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    get_ico_detail: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}call_report_data/propertyid/${payload.propertyid}/guid/${payload.guid}`)
          .then((r) => {
            resolve(r);
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    s3bucketaudio: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .post(`${AppSettings.API_HOST}audio`, {
            payload: payload,
          })
          .then((r) => {
            resolve(r);
            return r;
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    getNotifycompany_property_work_order: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}realpage_work/companies/${payload}`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    save_work_order: (context, payload) => {
      return new Promise((resolve, reject) => {
        let propertyid = payload.propertyid;
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .post(`${AppSettings.API_HOST}property/${propertyid}/orders`, {
            payload: JSON.stringify(payload),
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    save_yardi_work_order: (context, payload) => {
      return new Promise((resolve, reject) => {
        let propertyid = payload.propertyid;
        axios
          .post(`${AppSettings.API_HOST}property/${propertyid}/yardi/orders`, {
            payload: JSON.stringify(payload),
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    save_entrata_work_order: (context, payload) => {
      return new Promise((resolve, reject) => {
        let propertyid = payload.selected_propertyid;
        axios
          .post(`${AppSettings.API_HOST}property/${propertyid}/entrata/orders`, {
            payload: JSON.stringify(payload),
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    save_resman_work_order: (context, payload) => {
      return new Promise((resolve, reject) => {
        let propertyid = payload.propertyid;
        axios
          .post(`${AppSettings.API_HOST}property/${propertyid}/resman/orders`, {
            payload: JSON.stringify(payload),
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    getYardiCompany: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .post(`${AppSettings.API_HOST}yardipage_work/companies/getYardiProperty`, {
            payload: JSON.stringify(payload),
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    GetentrataResidents: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .post(`${AppSettings.API_HOST}entrata_company/property/residents`, {
            payload: payload,
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              reject(r);
            } else {
              context.commit("SET_ENTRATA_RESIDENTS", (r as any).data);
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    getentrataSite: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .post(`${AppSettings.API_HOST}entrata/company/get_site`, {
            payload: JSON.stringify(payload),
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    get_entrata_work_order_details: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}property/${payload.propertyid}/entrata/orders/${payload.guid}`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              //alert(JSON.stringify(r));
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    getResidentsTags: (context, payload) => {
      const propertyid = payload.propertyid;
      const company_id = payload.company_id;
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .post(`${AppSettings.API_HOST}company/${payload.company_id}/property/${payload.propertyid}/resident_tags`, {
            resident_ids: [],
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              reject(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    getOptinstatus: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}optinnumber/property/${payload}`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              // replayAction('getConversation',payload)
              reject(r);
            } else {
              context.commit("SET_USEROPTIN_STATUS", (r as any).data);
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    getOptinstatusByNumber: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}optinnumber/propertyid/${payload.propertyid}/phonenumber/${payload.phone}`)
          .then((r) => {
            resolve(r);
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    getNotifyResident: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}company/${payload.company_id}/property/${payload.property_id}/notify_residents`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              reject(r);
            } else {
              context.commit("SET_NOTIFY_RESIDENTS", (r as any).data);
              resolve(r);
            }
            //manage tags refresh
            //context.commit("SHOW_RESIDENT_INFOLOADER", false);
          })
          .catch((r) => {
            if (r.response && r.response.status === 401) {
              context.commit("LOGOUT_STATUS", true);
            }
            handle401(r, context);
            reject(r);
            //context.commit("SHOW_RESIDENT_INFOLOADER", false);
          });
      });
    },

    /*--------------------------------------------------------------------
    |                     Socket Communication
    --------------------------------------------------------------------*/

    async syncUnreadCounts(context: any, propertyId: string) {
      try {
        const email = context.state.notifyData.current_user?.email;
        const response = await axios.post(`${AppSettings.API_HOST}api/v1/getNotificationData`, { email, propids: [propertyId] }, { headers: { Authorization: `Bearer ${context.state.account.jwt}` } });
        const notifications = response.data?.data || [];
        const countMap: Record<string, number> = {};
        notifications.forEach((n: any) => {
          const channelId = n._source?.channelId;
          if (channelId && n._source?.readStatus === "unread") {
            countMap[channelId] = (countMap[channelId] || 0) + 1;
          }
        });
        context.commit("APPLY_UNREAD_COUNTS", countMap);
      } catch {
        // Non-fatal — inbox still shows without counts
      }
    },

    getInboxData: (context, payload) => {
      const { page = 0, propertyid } = payload;
      return axios
        .get(`${AppSettings.API_HOST}getInboxData`, {
          params: { from: page, propertyId: propertyid },
          headers: { Authorization: `Bearer ${context.state.account.jwt}` },
        })
        .then(({ data }) => {
          if (data.error === "401") {
            return Promise.reject(data);
          }

          const chatList = data.data.results?.map((chat) => ({ ...chat })) || [];
          context.commit("SET_USERCHATLIST", chatList);
          return data;
        });
    },

    updateUserChatList: (context, payload) => {
      const { message, propertyId } = payload;
      const { notifyData } = context.state;

      if (message.company_id !== notifyData.login_company_id || message.type === "optin") {
        return;
      }

      const chatList = notifyData.communicationUserchatList;
      const chatIndex = chatList.findIndex((chat) => chat._source.channelId === message.channel_id);

      const activeChannelId = notifyData.directChatParams?.channelId;
      const isActiveChannel = message.channel_id === activeChannelId;

      if (chatIndex !== -1) {
        const existing = chatList[chatIndex]._source;
        const currentUnreadCount = existing.unreadCount || 0;
        const updatedChat = {
          ...chatList[chatIndex],
          _source: {
            ...existing,
            msgBody: message.message.msgBody || "[Image]",
            timestamp: message.message.timestamp,
            readStatus: isActiveChannel ? "read" : "unread",
            status: message.status,
            media: message.message.media || [],
            unreadCount: isActiveChannel ? 0 : currentUnreadCount + 1,
          },
        };

        chatList.splice(chatIndex, 1);
        chatList.unshift(updatedChat);
        context.commit("SET_USERCHATLIST", chatList);
      } else {
        setTimeout(() => {
          context.dispatch("getInboxData", { page: 0, propertyid: propertyId });
        }, 1000);
      }
    },

    getConversation: (context, payload) => {
      const channelId = payload?.channelId;
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = `Bearer ${context.state.account.jwt}`;
        axios
          .get(`${AppSettings.API_HOST}getChatHistory?channelId=${channelId}`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              reject(r);
            } else {
              context.commit("SET_USERCHATHISTORY", (r as any).data);
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    getConversationByPhone: (context, payload) => {
      const to = payload?.to;
      const from = payload?.from;
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = `Bearer ${context.state.account.jwt}`;
        axios
          .get(`${AppSettings.API_HOST}getChatHistory?${`to=${to}&from=${from}`}`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              reject(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    createChatChannel: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = `Bearer ${context.state.account.jwt}`;
        axios
          .post(`${AppSettings.API_HOST}createChatChannel`, payload)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              reject(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            console.log(r, "check your error");
          });
      });
    },
    getResidentDetails: (context, payload) => {
      return new Promise((resolve, reject) => {
        const { propertyId, phone } = payload;
        axios.defaults.headers.common.Authorization = `Bearer ${context.state.account.jwt}`;
        axios
          .get(`${AppSettings.API_HOST}property/${propertyId}/phonenumber/${phone}/residentinfo`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              reject(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    initializeActiveProperty: (context, propertyid) => {
      const channelId = propertyid;
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = `Bearer ${context.state.account.jwt}`;
        axios
          .get(`${AppSettings.API_HOST}company/property/${propertyid}/propertyinfo`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              reject(r);
            } else {
              context.commit("SET_ACTIVE_PROPERTY", (r as any).data);
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    initializeActiveChat: (context, channelId) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = `Bearer ${context.state.account.jwt}`;
        axios
          .get(`${AppSettings.API_HOST}texting/${channelId}/channel_info`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              reject(r);
            } else {
              context.commit("SET_ACTIVE_CHAT", (r as any).data);
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    getTextMMSCount: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = `Bearer ${context.state.account.jwt}`;
        axios
          .get(`${AppSettings.API_HOST}company/${payload.companyId}/property/${payload.propertyId}/text/mms/count`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              reject(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    // Same FormData shape as notify-web-upgrade sendMassText so schedule_datetime,
    // timezone, and files land on the shared /api/v1/send/mass/message endpoint.
    sendMassText: (context, payload) => {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        for (const key in payload) {
          if (key === "files") {
            if (Array.isArray(payload.files)) {
              payload.files.forEach((file: File) => formData.append("files", file));
            }
          } else if (payload[key] !== undefined && payload[key] !== null) {
            formData.append(key, payload[key]);
          }
        }
        axios
          .post(`${AppSettings.API_HOST}api/v1/send/mass/message`, formData, {
            headers: {
              Authorization: `Bearer ${context.state.account.jwt}`,
              "Content-Type": "multipart/form-data",
            },
          })
          .then((r) => {
            if (r.data?.error === "401") {
              reject(r);
            } else {
              context.commit("SEND_MESSAGE", [r.data]);
              resolve(r);
            }
          })
          .catch((r) => {
            console.error("Error in sendMassText:", r);
            handle401(r, context);
            reject(r);
          });
      });
    },

    // Same list web Contacts uses for Mass Text history.
    getResidentCallingList: (context, payload) => {
      const params: Record<string, any> = {
        type: payload.type || "masstext",
        limit: payload.limit || 50,
      };
      if (payload.sort) params.sort = payload.sort;
      if (payload.order) params.order = payload.order;
      if (payload.cursor) {
        params.cursor = typeof payload.cursor === "string" ? payload.cursor : JSON.stringify(payload.cursor);
      }
      if (payload.search) params.search = payload.search;
      return axios
        .get(`${AppSettings.API_HOST}api/v1/residentcalling/${payload.property_id}`, {
          params,
          headers: { Authorization: `Bearer ${context.state.account.jwt}` },
        })
        .then((r) => {
          if (r.data?.error === "401") {
            return Promise.reject(r);
          }
          return r;
        })
        .catch((r) => {
          handle401(r, context);
          return Promise.reject(r);
        });
    },

    sendMessage: (context, payload) => {
      return new Promise((resolve, reject) => {
        const formData = util.createFormDataFromPayload(payload);
        axios
          .post(`${AppSettings.API_HOST}api/v1/send/message`, formData, {
            headers: {
              Authorization: `Bearer ${context.state.account.jwt}`,
              "Content-Type": "multipart/form-data",
            },
          })
          .then((r) => {
            if (r.data?.error === "401") {
              reject(r);
            } else {
              context.commit("SEND_MESSAGE", [r.data]);
              resolve(r);
            }
          })
          .catch((r) => {
            console.error("Error in sendMessage:", r);
            handle401(r, context);
            reject(r);
          });
      });
    },

    markChannelRead(context, channel_id) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = `Bearer ${context.state.account.jwt}`;
        axios
          .post(`${AppSettings.API_HOST}api/v1/updateChannelLastReadStatus`, {
            channelId: channel_id,
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            reject(r);
          });
      });
    },

    async markChannelNotificationRead(context: any, payload: { channelId: string }) {
      try {
        await axios.post(`${AppSettings.API_HOST}api/v1/markNotificationRead`, { channelId: payload.channelId }, { headers: { Authorization: `Bearer ${context.state.account.jwt}` } });
      } catch {
        // Non-fatal
      }
    },

    getNotifyUserSettings(context, payload) {
      if (!payload?.uid) {
        return Promise.resolve({});
      }
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}getnotifyusersettings/${payload.uid}`)
          .then((r) => {
            if ((r as any).data && (r as any).data.error !== "401") {
              context.commit("SET_USER_SETTINGS", (r as any).data.data ?? (r as any).data);
            }
            resolve(r);
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    translateText(context, payload) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = `Bearer ${context.state.account.jwt}`;
        axios
          .post(`${AppSettings.API_HOST}api/v1/translated/language/text`, {
            language: payload.language,
            text: payload.text,
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              reject(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    getChannelInfo(context, payload) {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .get(`${AppSettings.API_HOST}getChanelInfo`, {
            params: {
              from: payload.from,
              to: payload.to,
              propertyid: payload.propertyid,
            },
          })
          .then((r) => {
            resolve(r);
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    sendOptMessage: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + localStorage.id_token;
        axios
          .post(`${AppSettings.API_HOST}sendOptMessage`, payload)
          .then((r) => {
            resolve(r);
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },

    saveOptinStatus: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + localStorage.id_token;
        axios
          .post(`${AppSettings.API_HOST}optinnumber/${payload.propertyId}`, {
            payload: {
              list: [{ ...payload.payload }],
            },
          })
          .then((r) => {
            resolve(r);
          });
      });
    },
    sendOptinstatus: (context, payload) => {
      const propertyId = payload?.payload.list[0].propertyid;
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + localStorage.id_token;
        axios
          .post(`${AppSettings.API_HOST}optinnumber/${propertyId}`, payload)
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              // replayAction('getConversation',payload)
              reject(r);
            } else {
              context.commit("SET_USEROPTIN_STATUS", (r as any).data);
              resolve(r);
            }
          })
          .catch((r) => {
            console.log("error", r);

            handle401(r, context);
            reject(r);
          });
      });
    },
    update_work_order: (context, payload) => {
      return new Promise((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .patch(`${AppSettings.API_HOST}property/${payload.propertyid}/realpage/updateorder/${payload.guid}`, {
            payload: JSON.stringify(payload),
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    update_yardi_work_order: (context, payload) => {
      return new Promise((resolve, reject) => {
        let propertyid = payload.propertyid;
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .patch(`${AppSettings.API_HOST}property/${propertyid}/yardi/updateorder/${payload.guid}`, {
            payload: JSON.stringify(payload),
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    update_resman_work_order: (context, payload) => {
      return new Promise((resolve, reject) => {
        let propertyid = payload.propertyid;
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          .patch(`${AppSettings.API_HOST}property/${propertyid}/resman/updateorder/${payload.guid}`, {
            payload: JSON.stringify(payload),
          })
          .then((r) => {
            if ((r as any).data && (r as any).data.error === "401") {
              resolve(r);
            } else {
              resolve(r);
            }
          })
          .catch((r) => {
            handle401(r, context);
            reject(r);
          });
      });
    },
    translateTextElevenLabs: (context, payload) => {
      return new Promise<any>((resolve, reject) => {
        axios.defaults.headers.common.Authorization = "Bearer " + context.state.account.jwt;
        axios
          // .post(`${AppSettings.API_HOST}api/v1/elevenlab/translated/language/text`, {
          //   from_language: payload.from_language,
          //   to_language: payload.to_language,
          //   body: payload.body,
          // })
          .post(`${AppSettings.API_HOST}api/v1/translated/language/text`, {
            language: payload.to_language,
            text: payload.body,
          })
          .then((response) => {
            if (response.data && response.data.error === "401") {
              reject(response);
            } else {
              resolve(response);
            }
          })
          .catch((error) => {
            handle401(error, context);
            reject(error);
          });
      });
    },
  },
  modules: {},
});

// Wire the global axios 401 interceptor now that the store exists. The
// interceptor needs the store to dispatch generatecustomtokeninauth0 /
// commit LOGIN_SUCCESS / RESET_APP_STATE. See src/lib/axiosInterceptor.ts.
setupAxiosInterceptor(store);

export default store;
